/* eslint max-len: 1 */

import {
  Component,
  Element,
  h,
  Host,
  Listen,
  Prop,
  State,
  Watch,
} from '@stencil/core';
import { Loader } from '@googlemaps/js-api-loader';
// MarkerClusterer
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { responsive } from '../../global/utility/responsive/responsive';

@Component({
  tag: 'ui-surrounding-map',
  styleUrl: 'ui-surrounding-map.scss',
  shadow: true,
})
export class UiSurroundingMap {
  @Prop() apiKey = 'AIzaSyAjA94yU4pD8rV1NYAx4nLw27lSCL14UKQ';

  @Prop() componentWidth: number;

  @Prop() placeType?: string;

  @Prop() mainMarkerName: string;

  @Prop() mainMarkerAddress: string;

  @Prop() centerLongitude = 0;

  @Prop() centerLatitude = 0;

  @Prop() measurementSystem: 'metric' | 'imperial';

  @Prop() transitLayer: boolean;

  @Prop() startZoomLevel = 15;

  @Prop() mapHue: string;

  /**
   * Disables usercentrics dependency
   */
  @Prop() debug = false;

  @Element() el: HTMLElement;

  @State() maps: typeof google.maps;

  @State() map: google.maps.Map;

  private markers: google.maps.marker.AdvancedMarkerElement[] = [];

  private mainMarker: google.maps.marker.AdvancedMarkerElement;

  @State() results: google.maps.places.PlaceResult[] = [];

  @State() hasConsented = false;

  private lastOpenedInfoWindow: google.maps.InfoWindow;

  private lastSelectedMarker: HTMLUiMapMarkerElement;

  get center() {
    return new this.maps.LatLng(this.centerLatitude, this.centerLongitude);
  }

  render() {
    return (
      <Host>
        <div class="map"></div>
        <div
          class={{
            list: true,
            'missing-consent': !this.hasConsented && !this.debug,
          }}
        >
          {this.results?.map(this.createResultListItem.bind(this))}
          {(this.hasConsented || this.debug) && (
            <ui-link
              class="show-all-link"
              icon="position"
              href={`https://www.google.com/maps/search/${this.placeType}/@${this.centerLatitude},${this.centerLongitude},17z`}
              target="_blank"
            >
              Alle zeigen
            </ui-link>
          )}
        </div>
      </Host>
    );
  }

  async componentWillLoad() {
    // listen for UserCentrics consent
    document.body.addEventListener(
      'newConsentStatus',
      this.onNewConsentStatus.bind(this)
    );
    if (this.debug) {
      await this.initGMaps();
    }
  }

  @Watch('maps')
  componentDidLoad() {
    if (this.maps) {
      this.setupMap();
    }
    responsive(this.el);
  }

  @Watch('map')
  onMapsSetup() {
    if (this.map) {
      this.placeMainLocationMarker();
      this.addTransitLayer();
      this.searchNearby();
    }
  }

  @Watch('centerLongitude')
  @Watch('centerLatitude')
  onMainLocationChange() {
    this.map.setCenter(this.center);
    this.mainMarker.position = this.center;
    this.searchNearby();
  }

  @Listen('newConsentStatus', { target: 'window' })
  onNewConsentStatus(ev) {
    if (ev.detail.googleMaps) {
      this.initGMaps();
      this.hasConsented = true;
    }
  }

  async initGMaps() {
    const loader = new Loader({
      apiKey: this.apiKey,
      version: 'weekly',
      libraries: ['places', 'geometry', 'visualization', 'marker'],
      language: document.documentElement.lang,
    });
    const { maps } = await loader.load();
    this.maps = maps;
  }

  setupMap() {
    this.map = new this.maps.Map(this.el.shadowRoot.querySelector('.map'), {
      mapId: 'SURROUNDING-MAP',
      zoom: this.startZoomLevel,
      center: this.center,
      styles: this.mapHue && [
        {
          stylers: [
            { gamma: 1.47 },
            { hue: this.mapHue },
            { lightness: -15 },
            { saturation: -44 },
          ],
        },
      ],
    });
  }

  placeMainLocationMarker() {
    const mainMarker = document.createElement('div');
    mainMarker.innerHTML = '<ui-map-marker variant="main"></ui-map-marker>';
    this.mainMarker = new this.maps.marker.AdvancedMarkerElement({
      position: { lat: this.centerLatitude, lng: this.centerLongitude },
      title: this.mainMarkerName,
      map: this.map,
      content: mainMarker,
    });
    const mainInfoWindow = new this.maps.InfoWindow({
      content: `
      <ui-headline level=4>${this.mainMarkerName}</ui-headline>
      <ui-paragraph>${this.mainMarkerAddress}<ui-paragraph>`,
    });
    this.mainMarker.addListener('click', () => {
      if (this.lastOpenedInfoWindow) {
        this.lastOpenedInfoWindow.close();
      }
      mainInfoWindow.open({
        anchor: this.mainMarker,
        map: this.map,
        shouldFocus: false,
      });
      this.lastOpenedInfoWindow = mainInfoWindow;
    });
  }

  addTransitLayer(map?) {
    if (this.transitLayer) {
      const transitLayer = new this.maps.TransitLayer();
      transitLayer.setMap(map || this.map);
    }
  }

  searchNearby() {
    if (!this.placeType) {
      const defaultMap = new this.maps.Map(
        this.el.shadowRoot.querySelector('.map'),
        {
          zoom: this.startZoomLevel,
          center: { lat: this.centerLatitude, lng: this.centerLongitude },
        }
      );
      this.addTransitLayer(defaultMap);
    }
    if (this.placeType) {
      const placesService = new this.maps.places.PlacesService(this.map);
      placesService.nearbySearch(
        {
          location: { lat: this.centerLatitude, lng: this.centerLongitude },
          rankBy: google.maps.places.RankBy.DISTANCE,
          type: this.placeType,
          language: 'de',
        },
        this.onNearbyResults.bind(this)
      );
    }
  }

  onNearbyResults(
    results: google.maps.places.PlaceResult[],
    status: google.maps.places.PlacesServiceStatus
  ) {
    if (status === google.maps.places.PlacesServiceStatus.OVER_QUERY_LIMIT) {
      // Try again after some time because of GM request rate limit
      setTimeout(() => this.setupMap(), 300);
    } else if (status === google.maps.places.PlacesServiceStatus.OK) {
      this.results = results;
    } else {
      this.results = [];
      console.error('Nearby search status:', status);
    }
  }

  createResultListItem(result: google.maps.places.PlaceResult) {
    return (
      <li>
        <ui-link
          href={`https://www.google.com/maps/search/?api=1&query=${result.vicinity}&query_place_id=${result.place_id}`}
        >
          {result.name}
        </ui-link>
        <span class="distance">
          {this.getDistanceLabelFor(result)[this.measurementSystem]}
        </span>
        <span class="address">{result.vicinity}</span>
      </li>
    );
  }

  getDistanceLabelFor(result: google.maps.places.PlaceResult) {
    const metricDistance = Math.floor(
      google.maps.geometry.spherical.computeDistanceBetween(
        result.geometry.location,
        { lat: this.centerLatitude, lng: this.centerLongitude }
      )
    );
    return {
      metric: `${metricDistance} m`,
      imperial: `${Math.floor(3.28084 * metricDistance)} ft`,
    };
  }

  private placeTypeToIcon = {
    airport: 'airplane',
    atm: 'banknote-hand',
    art_gallery: 'image',
    aquarium: 'aquarium',
    bank: 'bank',
    bar: 'bottle-glass',
    bus_station: 'bus',
    church: 'church-cross',
    city_hall: 'city-hall',
    clothing_store: 'clothing',
    doctor: 'stethoscope',
    gym: 'dumbbell-weight',
    hospital: 'heart-curve',
    light_rail_station: 'train',
    lodging: 'house-bed',
    movie_theater: 'vodcast',
    museum: 'house-star',
    park: 'tree',
    parking: 'parking',
    post_office: 'letter-sent',
    restaurant: 'plate-cutlery',
    shopping_mall: 'shopping-bag',
    store: 'business',
    subway_station: 'train',
    supermarket: 'food',
    taxi_stand: 'taxi',
    tourist_attraction: 'eiffel-tower',
    train_station: 'train',
    transit_station: 'train',
    travel_agency: 'island',
    university: 'doctor-hat',
  };

  @Watch('results')
  async onResultsChange(results: google.maps.places.PlaceResult[]) {
    this.markers = [];
    results?.forEach((poi) => {
      const placeMarker = document.createElement('div');
      placeMarker.innerHTML = `<ui-map-marker variant="place">
        <ui-icon icon="${this.placeTypeToIcon[this.placeType]}" type="content"> </ui-icon>
      </ui-map-marker>`;

      const marker = new this.maps.marker.AdvancedMarkerElement({
        position: poi.geometry.location,
        title: poi.name,
        map: this.map,
        content: placeMarker,
      });

      const routeLink = `https://www.google.com/maps/dir/?api=1&origin=${this.centerLatitude},${this.centerLongitude}&destination=${poi.vicinity}&destination_place_id=${poi.place_id}`;

      const infoWindow = new this.maps.InfoWindow({
        content: `
        <div class='info-window'>
        ${poi.photos ? `<img src='${poi.photos?.[0].getUrl({ maxWidth: 100 })}' width='100'/>` : ''}
        <ui-headline level=4>${poi.name}</ui-headline>
        <ui-paragraph>${poi.vicinity}</ui-paragraph>
        <ui-link icon="position" href='${routeLink}' target='blank'>Routenplaner</ui-link>
        </div>`,
      });
      marker.addListener('click', () => {
        if (this.lastOpenedInfoWindow) {
          this.lastOpenedInfoWindow.close();
          this.lastSelectedMarker.state = null;
        }

        this.lastSelectedMarker =
          placeMarker.firstElementChild as unknown as HTMLUiMapMarkerElement;
        this.lastSelectedMarker.state = 'active';

        infoWindow.open({
          anchor: marker,
          map: this.map,
          shouldFocus: false,
        });
        this.lastOpenedInfoWindow = infoWindow;
        infoWindow.addListener('closeclick', () => {
          marker.focus();
          this.lastSelectedMarker.state = null;
        });
      });
      this.markers.push(marker);
    });

    // eslint-disable-next-line no-new
    new MarkerClusterer({
      markers: this.markers,
      map: this.map,
      renderer: {
        render: ({ count, position }) => {
          const clusterMarker = document.createElement('div');
          clusterMarker.innerHTML = `<ui-map-marker variant="cluster">${count}</ui-map-marker>`;

          // create marker using svg icon
          return new this.maps.marker.AdvancedMarkerElement({
            position,
            content: clusterMarker,
            title: `Cluster of ${count} markers`,
            // adjust zIndex to be above other markers
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
          });
        },
      },
    });
    if (!this.startZoomLevel) {
      this.zoomToEncompassMarkers();
    }
  }

  zoomToEncompassMarkers() {
    const bounds = new this.maps.LatLngBounds();

    bounds.extend(this.mainMarker.position);
    this.markers.forEach((marker) => {
      bounds.extend(marker.position);
    });

    this.map?.fitBounds(bounds);
    this.map?.setZoom(this.map.getZoom());
  }
}
